/**
 * Spec 041: one line of text per session event, plus the merge and the DOM
 * bound. Every field is read defensively: a missing or non-scalar field drops
 * its clause, `side` is upper-cased and never branched on, and an unknown type
 * renders as a plain muted line.
 *
 * Field names marked "illustrative" were not seen in the real session of
 * input 02 (only described in prose): `realized_pnl`, `duration_s`,
 * `estimated_jump_usd`, `from_raw_symbol`, `to_raw_symbol`, `fill_basis`, `late`.
 */
import { localClock } from './liveView'
import type { LiveEvent } from './types'

export const EVENTS_DOM_LIMIT = 100
export const EVENTS_MEMORY_LIMIT = 5000

export interface EventSummary {
  text: string
  kind: 'plain' | 'feed' | 'reject' | 'roll'
  muted: boolean
}

type Data = Record<string, unknown>

function scalar(data: Data, key: string): string | number | boolean | null {
  const v = data[key]
  return typeof v === 'string' || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)) ? v : null
}
const str = (d: Data, k: string): string | null => {
  const v = scalar(d, k)
  return typeof v === 'string' && v !== '' ? v : null
}
const num = (d: Data, k: string): number | null => {
  const v = scalar(d, k)
  return typeof v === 'number' ? v : null
}
const side = (d: Data): string | null => str(d, 'side')?.toUpperCase() ?? null
const qty = (d: Data): string | null => {
  const q = num(d, 'quantity')
  return q === null ? null : String(Number(q.toFixed(6)))
}
const money = (n: number): string => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const signed = (n: number): string => {
  const r = Math.round(n * 100) / 100
  return r === 0 ? '0.00' : r > 0 ? `+${money(r)}` : `-${money(Math.abs(r))}`
}
const join = (...parts: Array<string | null>): string => parts.filter((p): p is string => p !== null).join(' ')

function duration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`
}

export function eventSummary(event: LiveEvent): EventSummary {
  const d: Data = typeof event.data === 'object' && event.data !== null ? event.data : {}
  const plain = (text: string): EventSummary => ({ text, kind: 'plain', muted: false })
  switch (event.type) {
    case 'session_started':
      return plain('Session started')
    case 'warmup_finished':
      return plain('Warm-up finished')
    case 'trading_started':
      return plain('Trading started')
    case 'feed_state': {
      const state = str(d, 'state')
      const reason = str(d, 'reason')
      const text = `Feed ${state ?? '—'}${d.entries_blocked === true ? ', new entries blocked' : ''}${reason !== null ? ` (${reason})` : ''}`
      return { text, kind: 'feed', muted: false }
    }
    case 'signal':
      return plain(join('Signal', side(d), qty(d)))
    case 'order_submitted':
      return plain(join('Order submitted', side(d), qty(d)))
    case 'order_filled': {
      const price = num(d, 'price')
      const commission = num(d, 'commission')
      const basis = str(d, 'fill_basis')
      const basisText = basis === 'trade' ? ' by trade' : basis === 'bar' ? ' by bar' : basis === 'last_known_price' ? ' at last known price' : ''
      const text = `${join('Filled', side(d), qty(d))}${price !== null ? ` @ ${money(price)}` : ''}${commission !== null ? `, commission ${money(commission)}` : ''}${basisText}${d.late === true ? ' (late)' : ''}`
      return plain(text)
    }
    case 'order_canceled':
      return plain('Order canceled')
    case 'order_rejected': {
      const reason = str(d, 'reason')
      return { text: `Order rejected${reason !== null ? `: ${reason}` : ''}`, kind: 'reject', muted: false }
    }
    case 'order_cut':
      return plain('Order cut by the contract limit')
    case 'position_opened':
    case 'position_changed': {
      const price = num(d, 'avg_price')
      const head = event.type === 'position_opened' ? 'Position opened' : 'Position changed'
      return plain(`${join(head, side(d), qty(d))}${price !== null ? ` @ ${money(price)}` : ''}`)
    }
    case 'position_closed': {
      const pnl = num(d, 'realized_pnl')
      const commission = num(d, 'commission')
      const held = num(d, 'duration_s')
      return plain(
        `Position closed${pnl !== null ? `, realized ${signed(pnl)} paper` : ''}${commission !== null ? `, commission ${money(commission)}` : ''}${held !== null ? `, held ${duration(held)}` : ''}`,
      )
    }
    case 'roll': {
      const from = str(d, 'from_raw_symbol')
      const to = str(d, 'to_raw_symbol')
      const jump = num(d, 'estimated_jump_usd')
      const text = `Contract roll${from !== null || to !== null ? ` ${from ?? '—'} → ${to ?? '—'}` : ''}${jump !== null ? `, P&L may jump about ${signed(jump)} USD without a trade` : ''}`
      return { text, kind: 'roll', muted: false }
    }
    case 'bar_repaired':
      return plain('Bar repaired')
    case 'trades_gap':
      return plain('Gap in trade data')
    case 'stop_requested':
      return plain('Stop requested')
    case 'session_ended':
      return plain('Session ended')
    default: {
      const pairs: string[] = []
      for (const key of Object.keys(d)) {
        if (pairs.length === 3) break
        const v = scalar(d, key)
        if (v !== null) pairs.push(`${key}=${String(v)}`)
      }
      const type = typeof event.type === 'string' && event.type !== '' ? event.type : 'event'
      return { text: pairs.length > 0 ? `${type} ${pairs.join(' ')}` : type, kind: 'plain', muted: true }
    }
  }
}

export function eventTime(event: LiveEvent): string {
  const first = localClock(event.at)
  return first !== '—' ? first : localClock(event.wall_at)
}

export function mergeEvents(held: LiveEvent[], page: LiveEvent[]): { events: LiveEvent[]; droppedOlder: boolean } {
  const bySeq = new Map<number, LiveEvent>()
  for (const e of held) bySeq.set(e.seq, e)
  for (const e of page) if (!bySeq.has(e.seq)) bySeq.set(e.seq, e)
  const all = [...bySeq.values()].sort((a, b) => a.seq - b.seq)
  const droppedOlder = all.length > EVENTS_MEMORY_LIMIT
  return { events: droppedOlder ? all.slice(all.length - EVENTS_MEMORY_LIMIT) : all, droppedOlder }
}

export function newestFirst(events: LiveEvent[], shown: number): LiveEvent[] {
  return events.slice(Math.max(0, events.length - shown)).reverse()
}
